import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { canWrite, personName, usePortalIdentity } from "../auth/usePortalIdentity";
import { useTenantRoster } from "../data/tenantRoster";
import {
  requestMonthUsageKey,
  requestsKey,
  requestThreadsPrefix,
  useRequests,
  useServices,
  type ClientRequest
} from "../data/requestQueries";
import { requestAttachmentsPrefix } from "../data/attachmentQueries";
import { usePortalChannel } from "../realtime/usePortalChannel";
import { ClientHead } from "../ui/ClientHead";
import { BoxState } from "../ui/BoxState";
import { Panel } from "../ui/Panel";
import { RequestsListPage } from "./clientRequests/RequestsListPage";
import { RequestPage, RequestNotFound, type SplitLinks } from "./clientRequests/RequestPage";
import "./clientRequests/requests.css";
import { useClientPath, useViewOnly } from "../data/readSource";

/**
 * Build round A2, 2026-10-08: the requests a split connects one request to, from the client's own
 * list, which is the only list the client can read. A part whose original is not in the list (beyond
 * its 200 rows, or deleted, when the database sets the link null anyway) shows no "Del av" rather than
 * a number with no page behind it.
 */
function splitLinks(request: ClientRequest, rows: ClientRequest[]): SplitLinks {
  const original = request.split_from_request_id
    ? rows.find((row) => row.id === request.split_from_request_id)
    : undefined;

  return {
    from: original ? { requestNumber: original.request_number } : null,
    into: rows
      .filter((row) => row.split_from_request_id === request.id)
      .map((row) => row.request_number)
      .sort((left, right) => left - right)
  };
}

/**
 * Ärenden, the client's requests, rebuilt after the sketch Mattej approved on 2026-09-24
 * (`prototype/kund/pages.js`, `requestsPage` and `requestPage`).
 *
 * **Two pages, one route component.** `routeTable.tsx` mounts this for both `/requests` and
 * `/requests/:number`, and the parameter decides which page is drawn. Until the second build round
 * the two urls were one screen, a list with the selected request beside it, and the first visible
 * row was selected when the url named none. The sketch made one request its own page
 * (FEATURE-MAP.md, question 8), which also removed every number the list and the detail used to
 * print twice side by side ("Numbers shown more than once", item 8). The narrow-viewport reveal that
 * scrolled a phone down to the detail went with the split: there is nothing under the list to reveal
 * any more, and the shell's `RouteChrome` already moves focus to the new page on every navigation.
 *
 * What is read here is read for both pages, so the list and the request share one cache and one
 * realtime subscription, and every state that is not a page of its own (loading, refused, failed) is
 * decided once, above the branch.
 *
 * What neither page has, per plan.md section 5.3: a due date, an ETA, a progress bar, an SLA
 * countdown, and any approval button for work that needs a separate price. The classification is
 * displayed. The change-order process that acts on it lives outside the portal, and
 * `tests/db/request-read-model.test.ts` holds the database to the same absence.
 */
export function RequestsRoute() {
  const identity = usePortalIdentity();
  const { number } = useParams();

  const person = personName(identity);
  const tenantId = identity.data?.tenantId ?? null;
  const requests = useRequests(tenantId);
  const services = useServices(tenantId);
  const roster = useTenantRoster(tenantId);
  const viewOnly = useViewOnly();
  const clientPath = useClientPath();

  // Not in "Visa som kund" (build round 41): the client's channel is the client's.
  usePortalChannel(tenantId && !viewOnly ? { kind: "tenant", id: tenantId, channel: "requests" } : null, {
    // Since build round A2 the month's count too: Petrovic's classification of a request changes it.
    // Since build round 54 every thread and every attachment set as well, by prefix: the event names
    // no request, and an open request showed Petrovic's new status in its pill while his reply waited
    // for the window to regain focus.
    invalidate: [
      requestsKey(tenantId ?? "none"),
      requestMonthUsageKey(tenantId ?? "none"),
      requestThreadsPrefix,
      requestAttachmentsPrefix
    ]
  });

  /**
   * The request page's head before the request is known: the way back and the name the browser tab
   * already gives it. The list's head is the page's name and nothing else.
   */
  const head = number ? (
    <ClientHead title={`Ärende ${number}`} titleId="request-title" back={{ to: clientPath("/requests"), label: "Ärenden" }} />
  ) : (
    <ClientHead title="Ärenden" titleId="requests-title" />
  );

  /**
   * The timeline waits for the roster, and so does the list, so both pages open in one piece.
   *
   * `isLoading` is the wrong test. A query disabled for want of a tenant is pending with nothing in
   * flight, so `isLoading` is false while `data` is still undefined, and a timeline drawn on that
   * would call every colleague Petrovic and then quietly correct itself. The resolved map is the
   * test. The tenant guard keeps the no-tenant case falling through to its own refusal below.
   */
  const rosterPending = Boolean(tenantId) && !roster.isError && roster.data === undefined;

  if (identity.isLoading || requests.isLoading || rosterPending) {
    return (
      <Frame head={head}>
        {/* Labelled for the browser suite and the attribution tests, which wait on this name. */}
        <section className="panel" aria-label="Ärenden laddas" aria-busy="true">
          <BoxState kind="loading" title="Hämtar ärendena" />
        </section>
      </Frame>
    );
  }

  if (!tenantId) {
    return (
      <Frame head={head}>
        <Panel>
          <BoxState
            kind="denied"
            title="Du saknar behörighet till den här vyn"
            body="Ditt konto har inte tillgång till ärenden för den här organisationen. Kontakta er behöriga kontakt om detta verkar fel."
          />
        </Panel>
      </Frame>
    );
  }

  if (requests.isError) {
    return (
      <Frame head={head}>
        <Panel>
          <BoxState
            kind="error"
            title="Något gick fel"
            body="Kontrollera anslutningen och försök igen. Inget har ändrats eller skickats."
            onRetry={() => void requests.refetch()}
          />
        </Panel>
      </Frame>
    );
  }

  // A failed roster read is a failure, never an empty roster. An empty one would rename every
  // colleague in every thread to Petrovic and look entirely healthy doing it.
  if (roster.isError || !roster.data) {
    return (
      <Frame head={head}>
        <Panel>
          <BoxState
            kind="error"
            title="Vi kan inte visa vem som skrivit vad"
            body="Namnen på personerna i er organisation kunde inte hämtas. Ärendena visas därför inte just nu."
            onRetry={() => void roster.refetch()}
          />
        </Panel>
      </Frame>
    );
  }

  const writable = canWrite(identity.data);
  const rows = requests.data ?? [];
  const serviceList = services.data ?? [];
  const serviceName = (id: string) =>
    serviceList.find((service) => service.id === id)?.display_name ?? "Tjänst";

  if (!number) {
    return (
      <RequestsListPage
        rows={rows}
        services={serviceList}
        tenantId={tenantId}
        userId={identity.data?.userId ?? null}
        writable={writable}
      />
    );
  }

  const request = rows.find((row) => String(row.request_number) === number);

  if (!request) {
    return <RequestNotFound number={number} />;
  }

  return (
    <RequestPage
      // Remounted per request, so one request's draft, files and idempotency key never carry over.
      key={request.id}
      request={request}
      tenantId={tenantId}
      serviceName={serviceName(request.service_id)}
      writable={writable}
      currentUserId={identity.data?.userId ?? null}
      /*
        Resolved rather than read straight off the identity. `identity.data?.displayName ?? null`
        was nameless for a profile that has a first name and no display name, and let a whitespace
        name through. `personName` trims and treats empty as absent.
      */
      displayName={person.kind === "named" ? person.display : null}
      roster={roster.data}
      split={splitLinks(request, rows)}
    />
  );
}

function Frame({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    <div className="k-page k-requests">
      {head}
      {children}
    </div>
  );
}
